import { useCallback, useEffect, useRef, useState } from "react";
import { getRuntimeApiBase } from "../lib/runtime-api-base";
import type { EquitiesSentimentPacket } from "../types/equities-sentiment";

const POLL_MS = 60_000;
const API_BASE = getRuntimeApiBase();

interface PacketResponse {
  ok?: boolean;
  packet?: EquitiesSentimentPacket;
}

function endpoint(symbol: string) {
  const params = new URLSearchParams({ symbol });
  return `${API_BASE}/api/equities-sentiment/packet?${params.toString()}`;
}

export function useEquitiesSentimentPacket(selectedSymbol: string) {
  const [packet, setPacket] = useState<EquitiesSentimentPacket | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const mountedRef = useRef(true);
  const symbolRef = useRef(selectedSymbol);
  symbolRef.current = selectedSymbol;

  const refresh = useCallback(async () => {
    try {
      setIsLoading(true);
      const response = await fetch(endpoint(symbolRef.current), {
        credentials: "include",
      });
      if (!mountedRef.current) return;
      if (!response.ok) throw new Error(`HTTP ${response.status}`);
      const body = (await response.json()) as PacketResponse;
      if (!body.packet) throw new Error("Missing sentiment packet");
      setPacket(body.packet);
      setError(null);
    } catch (err) {
      if (!mountedRef.current) return;
      setError(err instanceof Error ? err.message : "Packet fetch failed");
    } finally {
      if (mountedRef.current) setIsLoading(false);
    }
  }, []);

  useEffect(() => {
    mountedRef.current = true;
    void refresh();
    const id = setInterval(() => {
      if (document.visibilityState === "visible") void refresh();
    }, POLL_MS);
    return () => {
      mountedRef.current = false;
      clearInterval(id);
    };
  }, [refresh, selectedSymbol]);

  return { packet, refresh, isLoading, error };
}
